import { Component, OnDestroy, OnInit } from '@angular/core';
import { PageEvent } from '@angular/material/paginator';
import { Subject, takeUntil } from 'rxjs';
import { PageResponse } from 'src/app/auth/base-capacitor-http';
import { Trailer, TrailerSearchParams, TrailerSearchType, TrailerService, TrailerStatus, TrailerUpdateOption } from 'src/app/services/app/trailer.service';
import { Yard, YardSearchParams, YardSearchType, YardService, YardStatus, YardUpdateOption } from 'src/app/services/app/yard.service';
import { UtilitieService } from 'src/app/services/utils/utilities.service';
import { UpdateMessage, UpdateMessageType, WebsocketService } from 'src/app/services/utils/websocket.service';

@Component({
  selector: 'app-trailers',
  templateUrl: './trailers.component.html',
  styleUrls: ['./trailers.component.css'],
})
export class TrailersComponent implements OnInit, OnDestroy {
  trailerStatus: string[] = [];
  searchOptions: { display: string, value: string }[] = [{ display: 'Status', value: 'status' }, { display: 'Trailer', value: 'trailer' }];
  selectedSearchOption: string = this.searchOptions[0].value;
  // this will be used to store
  yardSearchParams: YardSearchParams;
  emptyYardPage: PageResponse<Yard>;
  pageSearchParams: TrailerSearchParams;
  page: PageResponse<Trailer>;
  pageSizeOptions = [5, 10, 25];
  destroy$: Subject<void> = new Subject<void>();

  constructor(private trailerService: TrailerService, private YardService: YardService, private utilService: UtilitieService, private websocketService: WebsocketService) { }

  statusDropDownSearch() {
    /*
    lost is the only trailer status that will not be listed in the trailer status
    rather to find a lost trailer that status is stored in the trailer location.
    */

    switch (this.selectedSearchOption) {
      case 'status':
        this.pageSearchParams.type = TrailerSearchType.STATUS;
        if (this.pageSearchParams.search === TrailerStatus.ALL) {
          this.pageSearchParams.type = TrailerSearchType.ALL;
        }
        break;
      case 'trailer':
        this.pageSearchParams.type = TrailerSearchType.TRAILER;
        break;

      default:
        this.pageSearchParams.type = TrailerSearchType.ALL;
        break;
    }

    this.loadTrailers();

  }


  setTruckStatus(trailer: Trailer) {
    // setting truck default status
    let truckColor = { color: 'green' };

    const statusColorMap = {
      [TrailerStatus.LOADED]: 'green',
      [TrailerStatus.EMPTY]: 'white',
      [TrailerStatus.OUT_OF_SERVICE]: 'red',
    };
    truckColor.color = statusColorMap[trailer.status];
    return truckColor;
  }

  async assignLostTrailer(lostTrailer: Trailer, emptyLocationIndex: number) {
    if (this.emptyYardPage.data.length === 0 || emptyLocationIndex === undefined) {
      return;
    }
    let yard = this.emptyYardPage.data[emptyLocationIndex];
    yard.trailer = lostTrailer;
    yard.trailerId = lostTrailer.id;
    lostTrailer.location = yard.location;
    await this.YardService.updateYard(YardUpdateOption.ASSIGN, yard);
    this.utilService.openSnackBar("Trailer has been assigned", '');

  }


  async checkoutTrailer(trailerIndex: number) {
    const result = await this.utilService.openConfirmationDialog('Trailer checkout', "Are you sure you want to checkout this trailer? It will remove the trailer and cannot be undone.")

    if (result) {
      const trailer = this.page.data[trailerIndex];
      await this.trailerService.updateTrailer(TrailerUpdateOption.CHECKOUT, trailer);
      this.page.data[trailerIndex].status = TrailerStatus.CHECKOUT;
      this.utilService.openSnackBar("Trailer has been checked out", '');
    }
  }

  async markTrailerLost(trailerIndex: number) {
    const result = await this.utilService.openConfirmationDialog('Lost trailer', 'Are you sure you want to mark this trailer as lost?');

    if (result) {
      const trailer = this.page.data[trailerIndex];
      await this.trailerService.updateTrailer(TrailerUpdateOption.LOST, trailer);
      trailer.location = TrailerStatus.LOST;
      this.page.data[trailerIndex] = trailer;

      this.utilService.openSnackBar('Trailer marked as missing', '');

    }

  }

  async loadEmptyYards() {
    this.emptyYardPage = await this.YardService.searchYards(this.yardSearchParams);
  }


  async loadTrailers() {
    this.page = await this.trailerService.searchTrailers(this.pageSearchParams);
  }

  async handlePageEvent(e: PageEvent) {
    this.pageSearchParams.size = e.pageSize;
    this.pageSearchParams.page = e.pageIndex;
    this.loadTrailers();
  }

  listenToUpdates() {
    this.websocketService.getUpdateMessages()
      .pipe(takeUntil(this.destroy$))
      .subscribe(update => {
        this.processUpdate(update);
      });
  }

  private processUpdate(update: UpdateMessage) {
    if (!update) {
      return;
    }
    switch (update.type) {
      case UpdateMessageType.TRAILER:
        this.processTrailerUpdate(update);
        break;
      case UpdateMessageType.YARD:
        this.processYardUpdate(update);
        break;

      default:
        break;

    }

  }

  processYardUpdate(update: UpdateMessage) {

    let yard: Yard = JSON.parse(update.data);
    if (yard.status !== YardStatus.EMPTY) {
      let index: number = this.emptyYardPage.data.findIndex(y => y.id === yard.id);
      if (index === -1) return;
      this.emptyYardPage.data.splice(index, 1);
      return;
    }

    this.emptyYardPage.data.push(yard);

  }

  processTrailerUpdate(update: UpdateMessage) {
    let trailer: Trailer = JSON.parse(update.data);
    let index: number;

    index = this.page.data.findIndex(t => t.id === trailer.id);
    if (index === -1) return;
    this.page.data[index] = trailer;


  }


  async ngOnInit(): Promise<void> {
    this.utilService.updateLoading(true);
    this.page = new PageResponse();
    this.emptyYardPage = new PageResponse();
    this.trailerStatus = this.trailerService.getAllTrailerStatus();

    this.yardSearchParams = {
      page: 0,
      size: 10,
      type: YardSearchType.STATUS,
      search: YardStatus.EMPTY
    };

    this.pageSearchParams = {
      page: 0,
      size: 10,
      type: TrailerSearchType.ALL,
      search: TrailerStatus.ALL
    }

    await this.loadTrailers();
    this.loadEmptyYards();
    this.listenToUpdates();
    this.utilService.updateLoading(false);
  }
  async ngOnDestroy(): Promise<void> {
    this.destroy$.next();
    this.destroy$.complete();
  }


}
